import { CategoryAxis, ColumnSeries, LineSeries, ValueAxis, XYChart } from "@amcharts/amcharts4/charts";
import { color, create, Sprite } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component } from "react";
import WebFont from "webfontloader";
import {
  BIG_NUMBER_ABBREVIATION,
  BLACK,
  BRIGHT_BLUE,
  BRIGHT_YELLOW,
  BROWN,
  CYAN,
  DARK_PURPLE,
  FOREST_GREEN,
  GRAY,
  GREEN,
  GREEN_HOLD,
  HUMMING_BIRD,
  INDIGO,
  MINT,
  ORANGE,
  RED,
  RUST,
  SERPIA,
  WHITE,
} from "../../../constants";
import LoadingEllipsis from "../../Loading/LoadingEllipsis";
import { columnSeriesConfiguration, configureCategoryAxis, configureValueAxis, lineSeriesConfiguration } from "../helpers/configurations";
import { legendConfigurationOCE, oceChartConfiguration } from "../helpers/oceConfigurations";
import { ThemeInt } from "../helpers/types";

interface Props {
  data: any;
  seriesList: Array<{ name: string; field: string; type: string }>;
  theme: ThemeInt;
  chartName: string;
  isLoading: boolean;
  categoryAxisField?: string;
}

const BAR_COLORS = [
  CYAN,
  BRIGHT_BLUE,
  GRAY,
  DARK_PURPLE,
  ORANGE,
  RED,
  GREEN,
  INDIGO,
  BROWN,
  BRIGHT_YELLOW,
  HUMMING_BIRD,
  RUST,
  GREEN_HOLD,
  SERPIA,
  MINT,
];

class ProductActivatedByMarketChart extends Component<Props> {
  constructor(props: Props) {
    super(props);
    this.tooltipConfig = this.tooltipConfig.bind(this);
  }

  chartId = this.props.chartName;
  chart!: XYChart;

  componentDidMount() {
    WebFont.load({
      custom: {
        families: ["nissan"],
        urls: ["../../../assets/fonts/fonts.css"],
      },
      // @ts-ignore
      active: this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    if (!_.isEqual(prevProps, this.props)) {
      this.chart.dispose();
      this.initChart();
    }
  }

  componentWillUnmount() {
    this.chart.dispose();
  }

  initChart() {
    const { data, seriesList, theme, categoryAxisField } = this.props;

    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);
    this.chart.data = data;
    this.chart.colors.list = BAR_COLORS.map((item) => color(item));

    // Creates Legend
    this.chart.legend = legendConfigurationOCE(theme);
    this.chart.legend.reverseOrder = false;

    // Create category chart axes
    const categoryAxis = this.chart.xAxes.push(new CategoryAxis());
    configureCategoryAxis(categoryAxis, theme);
    categoryAxis.renderer.minGridDistance = 20;
    categoryAxis.dataFields.category = categoryAxisField;
    categoryAxis.startLocation = 0.2;
    categoryAxis.renderer.labels.template.wrap = true;

    // Creates value axis
    const barValueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(barValueAxis, theme);
    barValueAxis.renderer.numberFormatter.numberFormat = "#.a";
    barValueAxis.renderer.numberFormatter.bigNumberPrefixes = BIG_NUMBER_ABBREVIATION;
    barValueAxis.renderer.minGridDistance = 1;
    barValueAxis.strictMinMax = true;

    const lineValueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(lineValueAxis, theme);
    lineValueAxis.renderer.numberFormatter.numberFormat = "#.a";
    lineValueAxis.renderer.numberFormatter.bigNumberPrefixes = BIG_NUMBER_ABBREVIATION;
    // lineValueAxis.syncWithAxis = barValueAxis;
    lineValueAxis.renderer.opposite = true;
    lineValueAxis.min = 0;
    lineValueAxis.strictMinMax = true;
    lineValueAxis.renderer.labels.template.adapter.add("text", function (text) {
      return `${text}%`;
    });

    seriesList?.forEach(({ name, field, type }, idx) => {
      const series = type == "bar" ? this.chart.series.push(new ColumnSeries()) : this.chart.series.push(new LineSeries());
      series.data = data;
      series.xAxis = categoryAxis;
      series.dataFields.categoryX = categoryAxisField;

      if (type == "bar" && series instanceof ColumnSeries) {
        columnSeriesConfiguration(series, {
          name: name,
          valueY: field,
        });
        series.stacked = true;
        series.yAxis = barValueAxis;
        // series.columns.template.tooltipText = "test";
        //@ts-ignore
        // series.columns.template.adapter.add("tooltipText", this.tooltipConfig);
      } else if (series instanceof LineSeries) {
        const bullet = lineSeriesConfiguration(series, {
          valueY: field,
          name: name,
        });
        series.yAxis = lineValueAxis;
        bullet.tooltipText = "{categoryX}: {valueY.formatNumber('#')}%";
        series.stroke = color(FOREST_GREEN);
        series.fill = color(FOREST_GREEN);
      }

      if (series.tooltip) {
        series.tooltip.getFillFromObject = false;
        series.tooltip.autoTextColor = false;
        series.tooltip.background.fill = color(WHITE);
        series.tooltip.label.fill = color(BLACK);
      }
    });

    // this.chart.series.each((series) => {
    //   series.bullets.getIndex(0)?.adapter.add("tooltipText", this.tooltipConfig);
    // });
  }

  tooltipConfig(value: string | undefined, target: Sprite) {
    const dataValue = target?.tooltipDataItem?.dataContext as { date: string; [index: string]: string | number };

    let text = `[bold]${dataValue[this.props.categoryAxisField as string]}[/]\n`;

    this.chart.series.each((item) => {
      const value = item?.dataFields?.valueY && dataValue ? (dataValue[item.dataFields.valueY] as number) : null;
      if (value) {
        text += `[${item.stroke}]●[/] ${item.name}: ${value ? `${Number(value).toLocaleString()}%` : "n/a"}\n`;
      }
    });

    return text;
  }

  render() {
    return (
      <div className={"chartWrapper"}>
        <div id={this.props.chartName} className={"graph"} />
        <LoadingEllipsis isLoading={this.props.isLoading} />
      </div>
    );
  }
}

export default ProductActivatedByMarketChart;
